// src/components/TemplateCard.tsx
// Reusable card for displaying a single template.

import { useI18n } from '@react/hooks';
import {
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@react/components';

import type { TemplateDefinition } from '@/features/templates/types';
import {
  getTemplateCategoryLabel,
  getTemplateScopeLabel,
  getTemplateCategoryIcon,
  getCategoryBadgeVariant,
  getScopeBadgeVariant,
} from '@/features/templates/logic';

// ---------------------------------------------------------------------------
// SVG icon helpers
// ---------------------------------------------------------------------------

function CategoryIcon({ category, className }: { category: string; className?: string }) {
  const iconPath = getTemplateCategoryIcon(category as any);
  if (!iconPath) return null;
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={iconPath} />
    </svg>
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
  );
}

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 3l14 9-14 9V3z" />
    </svg>
  );
}

function BarChartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 20V10M12 20V4M6 20v-6" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface TemplateCardProps {
  template: TemplateDefinition;
  onUse?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TemplateCard({
  template,
  onUse,
  onEdit,
  onDelete,
  onDuplicate,
}: TemplateCardProps) {
  const { t } = useI18n();

  return (
    <Card className="flex flex-col h-full transition-shadow hover:shadow-md">
      <CardHeader className="pb-2">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            {template.icon ? (
              <svg
                className="h-5 w-5 text-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d={template.icon} />
              </svg>
            ) : (
              <CategoryIcon category={template.category} className="h-5 w-5 text-primary" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm font-semibold truncate">
              {template.name}
            </CardTitle>
            <CardDescription className="text-xs line-clamp-2 mt-0.5">
              {template.description}
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 pb-2">
        <div className="flex flex-wrap gap-1.5 mt-1">
          <Badge variant={getCategoryBadgeVariant(template.category)} size="sm">
            {t(getTemplateCategoryLabel(template.category))}
          </Badge>
          <Badge variant={getScopeBadgeVariant(template.scope)} size="sm">
            {t(getTemplateScopeLabel(template.scope))}
          </Badge>
        </div>

        {typeof template.usage_count === 'number' && (
          <div className="flex items-center gap-1 mt-3 text-xs text-muted-foreground">
            <BarChartIcon className="h-3.5 w-3.5" />
            <span>
              {t('templates.usageCount')}: {template.usage_count}
            </span>
          </div>
        )}
      </CardContent>

      <CardFooter className="pt-2 border-t border-border flex items-center gap-1">
        {onUse && (
          <button
            type="button"
            onClick={onUse}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <PlayIcon className="h-3.5 w-3.5" />
            {t('templates.use')}
          </button>
        )}
        <div className="flex-1" />
        {onDuplicate && (
          <button
            type="button"
            onClick={onDuplicate}
            className="p-1.5 rounded-md text-muted-foreground transition-colors hover:text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t('templates.duplicate')}
          >
            <CopyIcon className="h-4 w-4" />
          </button>
        )}
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="p-1.5 rounded-md text-muted-foreground transition-colors hover:text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t('templates.edit')}
          >
            <PencilIcon className="h-4 w-4" />
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="p-1.5 rounded-md text-muted-foreground transition-colors hover:text-destructive hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t('templates.delete')}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
      </CardFooter>
    </Card>
  );
}
